/**
 * auth-link-error — CANONICAL handling of failed email-link verification.
 *
 * GoTrue reports a rejected email link by redirecting to the Site URL with the
 * failure in the URL *fragment*, e.g.
 *
 *   https://authoritylayer.app/#error=access_denied
 *     &error_code=otp_expired&error_description=Email+link+is+invalid+or+has+expired
 *
 * Before this module the app silently rendered the homepage and the customer
 * was stranded mid-funnel. This module owns:
 *
 *   1. detecting the error (fragment or query, both shapes GoTrue uses)
 *   2. resolving WHICH pending Free Scan the recovery belongs to — with a
 *      strict precedence order and a fail-closed rule. Never "latest", never
 *      "first", never arbitrary.
 *
 * Precedence (spec §3):
 *   A. explicit `scan_request_id` carried through the intended auth flow
 *   B. a pending-scan identity already persisted for THIS browser
 *   C. server lookup, only when EXACTLY ONE eligible pending request exists
 *   D. otherwise → ambiguous, fail closed and require explicit resolution
 *
 * PURE module: no React, no Supabase, no side effects.
 */

export type AuthLinkErrorCode =
  | "otp_expired"
  | "access_denied"
  | "invalid_request"
  | "unknown";

export interface AuthLinkError {
  /** Normalised code we branch on. */
  code: AuthLinkErrorCode;
  /** Raw `error` value from the provider, when present. */
  rawError: string | null;
  /** Raw `error_code` value from the provider, when present. */
  rawErrorCode: string | null;
  /** Provider description, already URL-decoded. */
  description: string | null;
  /** True when this is an expired/invalid email link (recoverable by resend). */
  recoverable: boolean;
}

const RECOVERABLE_CODES = new Set(["otp_expired", "access_denied", "invalid_request", "expired_token"]);

/**
 * Parse an auth link failure out of a location-like input. Accepts the raw
 * `hash` and/or `search` strings; either may carry the error depending on the
 * provider flow.
 */
export function parseAuthLinkError(input: {
  hash?: string | null;
  search?: string | null;
}): AuthLinkError | null {
  const candidates = [input.hash ?? "", input.search ?? ""]
    .map((s) => s.replace(/^[#?]/, ""))
    .filter((s) => s.length > 0);

  for (const candidate of candidates) {
    const params = new URLSearchParams(candidate);
    const rawError = params.get("error");
    const rawErrorCode = params.get("error_code");
    if (!rawError && !rawErrorCode) continue;

    const normalised = (rawErrorCode || rawError || "").toLowerCase();
    let code: AuthLinkErrorCode = "unknown";
    if (normalised.includes("otp_expired") || normalised.includes("expired")) code = "otp_expired";
    else if (normalised.includes("access_denied")) code = "access_denied";
    else if (normalised.includes("invalid")) code = "invalid_request";

    return {
      code,
      rawError: rawError ?? null,
      rawErrorCode: rawErrorCode ?? null,
      description: params.get("error_description"),
      recoverable:
        RECOVERABLE_CODES.has(normalised) ||
        code === "otp_expired" ||
        code === "access_denied" ||
        code === "invalid_request",
    };
  }
  return null;
}

export type RecoveryIdentitySource = "explicit" | "browser_session" | "server_unique" | "none";

export interface RecoveryIdentity {
  scanRequestId: string | null;
  email: string | null;
  source: RecoveryIdentitySource;
  /** True when more than one eligible request exists → require explicit input. */
  ambiguous: boolean;
}

/**
 * Resolve the Free Scan identity a recovery resend must reuse.
 *
 * `serverCandidates` is the FULL list of eligible pending scan request ids for
 * the address (already filtered server-side to `scan_id IS NULL`). More than
 * one → ambiguous; we never pick one.
 */
export function resolveRecoveryIdentity(args: {
  explicitScanRequestId?: string | null;
  persisted?: { scan_request_id: string; email?: string | null } | null;
  requestedEmail?: string | null;
  serverCandidates?: string[] | null;
}): RecoveryIdentity {
  const email = args.requestedEmail?.trim().toLowerCase() || args.persisted?.email?.trim().toLowerCase() || null;

  const explicit = args.explicitScanRequestId?.trim();
  if (explicit) {
    return { scanRequestId: explicit, email, source: "explicit", ambiguous: false };
  }

  const persistedId = args.persisted?.scan_request_id?.trim();
  if (persistedId) {
    return { scanRequestId: persistedId, email, source: "browser_session", ambiguous: false };
  }

  const candidates = (args.serverCandidates ?? []).filter(
    (id) => typeof id === "string" && id.trim().length > 0,
  );
  if (candidates.length === 1) {
    return { scanRequestId: candidates[0].trim(), email, source: "server_unique", ambiguous: false };
  }

  return {
    scanRequestId: null,
    email,
    source: "none",
    ambiguous: candidates.length > 1,
  };
}

/** Customer-facing copy. Single source so every surface says the same thing. */
export const AUTH_LINK_ERROR_COPY = {
  title: "That sign-in link is no longer valid.",
  body:
    "Sign-in links can only be used once, and they expire. We can send you a fresh link to the same address — your scan setup is saved and will continue exactly where it left off.",
  cta: "Send me a new link",
  ambiguous:
    "Enter the email address you used so we can send a new link to the right scan.",
  sent: "New link sent. Check your inbox and open the newest email.",
} as const;
